"use client";

import React, { useEffect } from "react";
import * as L from "leaflet";
import { useMap, useMapEvents } from "react-leaflet";
import { insideIcon, isInPolygon, outsideIcon } from "./location-picker-constants";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { LocateIcon } from "lucide-react";
import { useLocale } from "use-intl";
import { translations } from "@/messages";
type MapEventLayerRawProps = {
  markerRef: React.MutableRefObject<L.Marker<any> | undefined>;
  markerGroupsRef: React.MutableRefObject<L.LayerGroup<any> | undefined>;
  setZoom: (zoom: number) => void;
  locationPolygon: GeoJSON.Polygon | undefined;
  type: "static" | "dynamic" | undefined;
  disabled?: boolean;
};
let currentIcon: "inside" | "outside" = "inside";
const MapEventLayerRaw = ({
  markerRef,
  markerGroupsRef,
  setZoom,
  locationPolygon,
  type,
  disabled
}: MapEventLayerRawProps) => {
  const locale = useLocale() as keyof typeof translations;
  const mapObject = useMap();
  const map = useMapEvents({
    zoom: () => {
      const center = map.getCenter();
      setZoom(map.getZoom());
      if (!disabled) {
        markerRef.current?.setLatLng(center);
        updateMarkerIcon();
      }
    },
    drag: () => {
      if (disabled) {
        return;
      }
      const center = map.getCenter();
      markerRef.current?.setLatLng(center);
      updateMarkerIcon();
    },
    moveend: () => {
      const center = map.getCenter();
      setZoom(map.getZoom());
      if (!disabled) {
        markerRef.current?.setLatLng(center);
        updateMarkerIcon();
      }
    }
  });
  useEffect(() => {
    if (markerGroupsRef.current?.clearLayers) {
      markerGroupsRef.current?.clearLayers();
    }
    markerGroupsRef.current = L.layerGroup().addTo(map);
    markerRef.current = L.marker(map.getCenter(), {
      draggable: false
    }).addTo(markerGroupsRef.current);
    updateMarkerIcon();
    return () => {
      markerGroupsRef.current?.clearLayers && markerGroupsRef.current.clearLayers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  const updateMarkerIcon = () => {
    if (!markerRef.current?.getLatLng || !locationPolygon?.coordinates) {
      return;
    }
    const markerLatLng = markerRef.current.getLatLng();
    const isInside = !!type ? isInPolygon([markerLatLng.lat, markerLatLng.lng], locationPolygon) : true;
    if (isInside && currentIcon === "outside") {
      markerRef.current.setIcon(insideIcon as L.Icon);
      currentIcon = "inside";
    }
    if (!isInside && currentIcon === "inside") {
      markerRef.current.setIcon(outsideIcon as L.Icon);
      currentIcon = "outside";
    }
  };
  const navigateToUserLocation = () => {
    navigator.geolocation.getCurrentPosition(position => {
      mapObject.setView([position.coords.latitude, position.coords.longitude] as L.LatLngExpression, 15, {
        animate: true
      });
    }, () => {
      toast.warn(translations[locale]["formView"]["couldNotRetrieveUserLocation"]);
    });
  };
  return <div className="absolute right-2 top-2 z-[10000] flex flex-col gap-2" data-sentry-component="MapEventLayerRaw" data-sentry-source-file="MapEventLayerRaw.tsx">
      {!disabled && <>
          <Button className="bg-white text-black hover:bg-white hover:text-blue-600" onClick={navigateToUserLocation} type="button">
            <LocateIcon size={20} />
          </Button>
        </>}
    </div>;
};
export default MapEventLayerRaw;